import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiEdit2, FiCheck, FiX, FiPlus, FiSave } from "react-icons/fi";
import MonthlyBillingCard from "../components/admin/MonthlyBillingCard";
import BusinessTdsCard from "../components/admin/BusinessTdsCard";
import OpeningBalanceCard from "../components/admin/OpeningBalanceCard";
import API from "../utils/api";
import toast from "react-hot-toast";
import { useStaffLog } from "../utils/useStaffLog";
import { useAuth } from "../context/AuthContext";
import { canStaffAccess } from "../utils/staffPages";

const COMPANY_TYPES = {
  sole_proprietorship: "Sole Proprietorship",
  partnership: "Partnership",
  private_limited: "Private Limited Company",
  public_limited: "Public Limited Company",
  llp: "LLP",
  ngo: "NGO / Trust",
  other: "Other",
};

const UPPERCASE_KEYS = ["gstNumber", "panNumber", "tanNumber", "ifscCode", "udyamNumber"];
const SYNC_LABELS = { companyName: "Company name", gstNo: "GST number", companyAddress: "Address", customerPhone: "Phone", customerEmail: "Email" };
const DOC_FIELDS = [
  { label: "Company Logo", field: "logoUrl", isImage: true },
  { label: "GST Certificate", field: "gstDocUrl" },
  { label: "PAN Certificate", field: "panDocUrl" },
  { label: "TAN Certificate", field: "tanDocUrl" },
  { label: "Udyam Certificate", field: "udyamDocUrl" },
];

const EMPTY_CONTACT = {
  name: "", designation: "", department: "", phone: "",
  email: "", isPrimary: false, notes: "",
};

/* ── Tiny reusable label+value pair ── */
const InfoField = ({ label, value, mono }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>
      {label}
    </div>
    <div style={{ fontSize: "0.9rem", fontFamily: mono ? "monospace" : "inherit", color: value ? "#111" : "#ccc", fontStyle: value ? "normal" : "italic" }}>
      {value || "—"}
    </div>
  </div>
);

/* ── Editable field: shows value or input depending on editMode ── */
const EditableField = ({ label, value, fieldKey, editForm, setEditForm, editMode, type = "text", mono, options }) => {
  if (!editMode) return <InfoField label={label} value={value} mono={mono} />;
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 4 }}>
        {label}
      </label>
      {options ? (
        <select
          className="form-control"
          value={editForm[fieldKey] ?? ""}
          onChange={(e) => setEditForm((p) => ({ ...p, [fieldKey]: e.target.value }))}
          style={{ width: "100%", boxSizing: "border-box", fontSize: "0.85rem" }}
        >
          <option value="">— Select —</option>
          {Object.entries(options).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          className="form-control"
          value={editForm[fieldKey] ?? ""}
          onChange={(e) => {
            const v = UPPERCASE_KEYS.includes(fieldKey) ? e.target.value.toUpperCase() : e.target.value;
            setEditForm((p) => ({ ...p, [fieldKey]: v }));
          }}
          style={{ width: "100%", boxSizing: "border-box", fontSize: "0.85rem", fontFamily: mono || UPPERCASE_KEYS.includes(fieldKey) ? "monospace" : "inherit" }}
        />
      )}
    </div>
  );
};

/* ── Section card wrapper ── */
const Card = ({ children, style }) => (
  <div style={{
    background: "#fff", border: "1px solid var(--border)",
    borderRadius: "var(--radius-lg)", padding: "22px 24px",
    ...style,
  }}>
    {children}
  </div>
);

/* ── Section heading ── */
const SectionTitle = ({ icon, children, editMode, onEdit, onSave, onCancel, saving }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, paddingBottom: 10, borderBottom: "1px solid var(--border)" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {icon && <span style={{ color: "var(--primary)", fontSize: "1rem" }}>{icon}</span>}
      <span style={{ fontWeight: 700, fontSize: "0.92rem", color: "#111" }}>{children}</span>
    </div>
    {onEdit && !editMode && (
      <button onClick={onEdit} style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 12px", border: "1px solid var(--border)", borderRadius: 6, cursor: "pointer", fontSize: "0.78rem", background: "#fff", fontFamily: "var(--font-body)", color: "var(--text-muted)" }}>
        <FiEdit2 size={12} /> Edit
      </button>
    )}
    {onEdit && editMode && (
      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={onSave} disabled={saving} style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 12px", border: "none", borderRadius: 6, cursor: "pointer", fontSize: "0.78rem", background: "var(--primary)", color: "#fff", fontFamily: "var(--font-body)", fontWeight: 600 }}>
          <FiSave size={12} /> {saving ? "Saving…" : "Save"}
        </button>
        <button onClick={onCancel} style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", border: "1px solid var(--border)", borderRadius: 6, cursor: "pointer", fontSize: "0.78rem", background: "#fff", fontFamily: "var(--font-body)" }}>
          <FiX size={12} /> Cancel
        </button>
      </div>
    )}
  </div>
);
// ── Indian number format: 10000 → "10,000"
const formatINR = (amount) =>
  Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 });

// ── Amount in words (Indian style)
const numberToWords = (num) => {
  if (!num || num === 0) return '';
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convert = (n) => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convert(n % 100) : '');
    if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
    if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
    return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
  };

  return convert(Math.floor(num)) + ' Rupees';
};
// Format number to Indian style while typing: 10000 → "10,000"
const formatINRInput = (value) => {
  const num = value.replace(/,/g, ''); // strip existing commas
  if (!num || isNaN(num)) return value;
  return Number(num).toLocaleString('en-IN');
};

// Strip commas before saving: "10,000" → 10000
const parseINRInput = (value) => value.replace(/,/g, '');

const AdminBusinessDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { log } = useStaffLog();
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";
  // Opening balance: admin, or staff with Businesses edit access
  const canEditOpeningBalance = isAdmin || (currentUser?.role === "staff" && canStaffAccess(currentUser?.allowedPages, "businesses", "edit"));
  const [selected, setSelected] = useState(null);

  // Company name edit (header)
  const [nameEdit, setNameEdit] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [nameSaving, setNameSaving] = useState(false);
  // Logo / document replace
  const [docBusy, setDocBusy] = useState("");
  const docInputs = useRef({});
  // "Update existing documents" panel
  const [sync, setSync] = useState(null);
  const [loading, setLoading] = useState(true);

  /* ── Credit ── */
  const [creditEditMode, setCreditEditMode] = useState(false);
  const [credit, setCredit] = useState({ creditLimit: "", creditTenureDays: "", creditTerms: "" });
  const [savedCredit, setSavedCredit] = useState({ creditLimit: null, creditTenureDays: null, creditTerms: "" });
  const [creditSaving, setCreditSaving] = useState(false);
  const previousCreditLimit = useRef(null);
  const [isVerifiedLive, setIsVerifiedLive] = useState(false);
  const [creditSummary, setCreditSummary] = useState(null);
  const [creditSummaryLoading, setCreditSummaryLoading] = useState(false);

  /* ── Contacts ── */
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT);
  const [savingContact, setSavingContact] = useState(false);
  
  /* ── Notes ── */
  const [notesEditMode, setNotesEditMode] = useState(false);
  const [notesValue, setNotesValue] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  /* ── Section edit states ── */
  const [contactInfoEdit, setContactInfoEdit] = useState(false);
  const [contactInfoForm, setContactInfoForm] = useState({});
  const [contactInfoSaving, setContactInfoSaving] = useState(false);

  const [taxEdit, setTaxEdit] = useState(false);
  const [taxForm, setTaxForm] = useState({});
  const [taxSaving, setTaxSaving] = useState(false);

  const [companyInfoEdit, setCompanyInfoEdit] = useState(false);
  const [companyInfoForm, setCompanyInfoForm] = useState({});
  const [companyInfoSaving, setCompanyInfoSaving] = useState(false);

  const [primaryContactEdit, setPrimaryContactEdit] = useState(false);
  const [primaryContactForm, setPrimaryContactForm] = useState({});
  const [primaryContactSaving, setPrimaryContactSaving] = useState(false);

  const [regAddressEdit, setRegAddressEdit] = useState(false);
  const [regAddressForm, setRegAddressForm] = useState({});
  const [regAddressSaving, setRegAddressSaving] = useState(false);

  const [commAddressEdit, setCommAddressEdit] = useState(false);
  const [commAddressForm, setCommAddressForm] = useState({});
  const [commAddressSaving, setCommAddressSaving] = useState(false);

  const [bankEdit, setBankEdit] = useState(false);
  const [bankForm, setBankForm] = useState({});
  const [bankSaving, setBankSaving] = useState(false);

  /* ── Load ── */
  useEffect(() => {
    setLoading(true);
    API.get(`/business/${id}`)
      .then((r) => {
        const biz = r.data.business || r.data;
        setSelected(biz);
        setIsVerifiedLive(!!biz.isVerified);
        setNotesValue(biz.notes || "");
        const hasCreditSaved = biz.creditLimit != null || biz.creditTenureDays != null || biz.creditTerms;
        setCreditEditMode(!hasCreditSaved && !biz.monthlyBillingEnabled);
        setCredit({ creditLimit: biz.creditLimit ?? "", creditTenureDays: biz.creditTenureDays ?? "", creditTerms: biz.creditTerms ?? "" });
        setSavedCredit({ creditLimit: biz.creditLimit, creditTenureDays: biz.creditTenureDays, creditTerms: biz.creditTerms ?? "" });
        previousCreditLimit.current = biz.creditLimit;
        // Init section forms
        setContactInfoForm({ contactPersonEmail: biz.contactPersonEmail || "", contactPersonPhone: biz.contactPersonPhone || "", website: biz.website || "" });
        setTaxForm({ gstNumber: biz.gstNumber || "", panNumber: biz.panNumber || "", tanNumber: biz.tanNumber || "" });
        setCompanyInfoForm({ companyType: biz.companyType || "", industry: biz.industry || "", udyamNumber: biz.udyamNumber || "" });
        setPrimaryContactForm({ contactPersonName: biz.contactPersonName || "", contactPersonDesignation: biz.contactPersonDesignation || "", contactPersonPhone: biz.contactPersonPhone || "", contactPersonEmail: biz.contactPersonEmail || "" });
        setRegAddressForm({ regAddressLine1: biz.regAddressLine1 || "", regAddressLine2: biz.regAddressLine2 || "", regCity: biz.regCity || "", regState: biz.regState || "", regPincode: biz.regPincode || "" });
        setCommAddressForm({ commAddressLine1: biz.commAddressLine1 || "", commAddressLine2: biz.commAddressLine2 || "", commCity: biz.commCity || "", commState: biz.commState || "", commPincode: biz.commPincode || "" });
        setBankForm({ bankName: biz.bankName || "", branchName: biz.branchName || "", ifscCode: biz.ifscCode || "", accountNumber: biz.accountNumber || "" });
      })
      .catch(() => toast.error("Failed to load business"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const hasCreditSaved = savedCredit.creditLimit != null || savedCredit.creditTenureDays != null || savedCredit.creditTerms;
    if (id && hasCreditSaved) {
      setCreditSummaryLoading(true);
      API.get(`/sales-orders/credit-summary/${id}`)
        .then(({ data }) => setCreditSummary(data))
        .catch(() => setCreditSummary(null))
        .finally(() => setCreditSummaryLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, savedCredit.creditLimit]);

  

  const fetchContacts = async () => {
    setContactsLoading(true);
    try {
      const r = await API.get(`/business/${id}/contacts`);
      setContacts(r.data.contacts || []);
    } catch { toast.error("Failed to load contacts"); }
    finally { setContactsLoading(false); }
  };

  const handleVerify = async (isVerified) => {
    try {
      await API.put(`/business/${id}/verify`, { isVerified, notes: notesValue });
      setIsVerifiedLive(isVerified);
      setSelected((s) => ({ ...s, isVerified }));
      toast.success(isVerified ? "✅ Business approved!" : "❌ Approval revoked!");
      await log(isVerified ? "approved_business" : "revoked_business", `${isVerified ? "Approved" : "Revoked"} business: ${selected?.companyName || id}`, "Business", Number(id));
    } catch { toast.error("Failed."); }
  };

  /* ── Section save via the admin edit endpoint (does not touch approval) ── */
  const saveSection = async (fields, setSaving, setEditMode, successMsg) => {
    setSaving(true);
    try {
      const r = await API.put(`/business/${id}/admin`, fields);
      const biz = r.data.business || r.data;
      setSelected(biz);
      setEditMode(false);
      toast.success(successMsg || "Saved!");
      await log("updated_business", `Updated business details: ${biz?.companyName || id}`, "Business", Number(id));
      if (r.data.sync?.needed) openSyncPanel(r.data.sync);
      return true;
    } catch (err) {
      const d = err?.response?.data;
      toast.error((Array.isArray(d?.errors) && d.errors.join(" ")) || d?.message || "Failed to save.");
      return false;
    } finally { setSaving(false); }
  };

  const saveCompanyName = async () => {
    const ok = await saveSection({ companyName: nameValue }, setNameSaving, setNameEdit, "Company name saved!");
    return ok;
  };

  /* ── Logo / certificate replace + remove ── */
  const uploadDoc = async (field, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File must be under 10 MB."); return; }
    setDocBusy(field);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await API.post("/upload/business-document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!up.data?.success) throw new Error("Upload failed");
      const r = await API.put(`/business/${id}/admin`, { [field]: up.data.url });
      setSelected(r.data.business || r.data);
      toast.success("File updated.");
      await log("updated_business", `Updated ${field} for business: ${selected?.companyName || id}`, "Business", Number(id));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not upload the file.");
    } finally {
      setDocBusy("");
      if (docInputs.current[field]) docInputs.current[field].value = "";
    }
  };

  const removeDoc = async (field, label) => {
    if (!window.confirm(`Remove ${label}?`)) return;
    setDocBusy(field);
    try {
      const r = await API.put(`/business/${id}/admin`, { [field]: null });
      setSelected(r.data.business || r.data);
      toast.success(`${label} removed.`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not remove the file.");
    } finally { setDocBusy(""); }
  };

  /* ── Update existing orders / invoices / receipts after an edit ── */
  const openSyncPanel = async (info) => {
    try {
      const r = await API.get(`/business/${id}/sync-preview`);
      const c = r.data.counts || {};
      const total = (c.ordersOpen || 0) + (c.ordersFinal || 0) + (c.billsFinal || 0) + (c.receiptsFinal || 0);
      if (!total) return;
      setSync({
        info, counts: c, busy: false, confirm: false,
        opts: { openOrders: true, finalOrders: false, bills: false, receipts: false },
      });
    } catch { /* preview is optional — the business itself is already saved */ }
  };

  const runSync = async () => {
    if (!sync) return;
    const { opts, info } = sync;
    const wantsFinal = opts.finalOrders || opts.bills || opts.receipts;
    if (wantsFinal && !sync.confirm) { toast.error("Please tick the confirmation box first."); return; }
    setSync((s) => ({ ...s, busy: true }));
    try {
      const r = await API.post(`/business/${id}/sync-documents`, { ...opts, fields: info.changedFields, confirm: sync.confirm });
      const u = r.data.updated || {};
      toast.success(`Updated: ${u.openOrders || 0} open orders, ${u.finalOrders || 0} delivered orders, ${u.bills || 0} invoices, ${u.receipts || 0} receipts.`);
      await log("synced_business_documents", `Applied edited details to existing documents: ${selected?.companyName || id} (${JSON.stringify(u)})`, "Business", Number(id));
      setSync(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not update the documents.");
      setSync((s) => (s ? { ...s, busy: false } : s));
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await API.put(`/business/${id}/verify`, { isVerified: isVerifiedLive, notes: notesValue });
      setSelected((s) => ({ ...s, notes: notesValue }));
      setNotesEditMode(false);
      toast.success("Notes saved!");
      await log("updated_business_notes", `Updated notes for business: ${selected?.companyName || id}`, "Business", Number(id));
    } catch { toast.error("Failed to save notes."); }
    finally { setSavingNotes(false); }
  };

  const saveCredit = async () => {
    setCreditSaving(true);
    try {
      const payload = {
        isVerified: isVerifiedLive, notes: notesValue,
        creditLimit: credit.creditLimit === "" ? null : Number(credit.creditLimit),
        creditTenureDays: credit.creditTenureDays === "" ? null : Number(credit.creditTenureDays),
        creditTerms: credit.creditTerms || null,
      };
      await API.put(`/business/${id}/verify`, payload);
      setSavedCredit({ creditLimit: payload.creditLimit, creditTenureDays: payload.creditTenureDays, creditTerms: payload.creditTerms ?? "" });
      setCreditEditMode(false);
      toast.success("Credit settings saved!");
      await log("updated_credit", `Updated credit settings for: ${selected?.companyName || id}`, "Business", Number(id));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save credit settings.");
    } finally { setCreditSaving(false); }
  };

  const deleteCredit = async () => {
    if (!window.confirm("Remove credit facility for this business?")) return;
    setCreditSaving(true);
    try {
      await API.put(`/business/${id}/verify`, { isVerified: isVerifiedLive, notes: notesValue, creditLimit: null, creditTenureDays: null, creditTerms: null });
      setCredit({ creditLimit: "", creditTenureDays: "", creditTerms: "" });
      setSavedCredit({ creditLimit: null, creditTenureDays: null, creditTerms: "" });
      setCreditEditMode(true);
      toast.success("Credit facility removed.");
    } catch (err) { toast.error(err?.response?.data?.message || "Failed."); }
    finally { setCreditSaving(false); }
  };

  const toggleCreditActive = async () => {
    const isDeactivated = savedCredit.creditLimit === 0;
    const newLimit = isDeactivated ? previousCreditLimit.current : 0;
    if (!isDeactivated) previousCreditLimit.current = savedCredit.creditLimit;
    setCreditSaving(true);
    try {
      await API.put(`/business/${id}/verify`, { isVerified: isVerifiedLive, notes: notesValue, creditLimit: newLimit, creditTenureDays: savedCredit.creditTenureDays, creditTerms: savedCredit.creditTerms || null });
      setSavedCredit((s) => ({ ...s, creditLimit: newLimit }));
      setCredit((c) => ({ ...c, creditLimit: newLimit ?? "" }));
      toast.success(isDeactivated ? "Credit reactivated." : "Credit deactivated.");
    } catch (err) { toast.error(err?.response?.data?.message || "Failed."); }
    finally { setCreditSaving(false); }
  };

  const openAddContact = () => { setContactForm(EMPTY_CONTACT); setEditingContact(null); setShowContactForm(true); };
  const openEditContact = (c) => {
    setContactForm({ name: c.name, designation: c.designation || "", department: c.department || "", phone: c.phone || "", email: c.email || "", isPrimary: c.isPrimary, notes: c.notes || "" });
    setEditingContact(c); setShowContactForm(true);
  };

  const saveContact = async () => {
    if (!contactForm.name.trim()) { toast.error("Name is required"); return; }
    setSavingContact(true);
    try {
      if (editingContact) {
        await API.put(`/business/${id}/contacts/${editingContact.id}`, contactForm);
        toast.success("Contact updated");
      } else {
        await API.post(`/business/${id}/contacts`, contactForm);
        toast.success("Contact added");
      }
      setShowContactForm(false); fetchContacts();
    } catch { toast.error("Failed to save contact"); }
    finally { setSavingContact(false); }
  };

  const deactivateContact = async (c) => {
    const isActive = c.isActive !== false; // treat undefined as active
    try {
      await API.put(`/business/${id}/contacts/${c.id}`, { ...c, isActive: !isActive });
      toast.success(isActive ? "Contact deactivated." : "Contact reactivated.");
      fetchContacts();
    } catch { toast.error("Failed to update contact status."); }
  };

  /* ── Loading / not found ── */
  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
      <div className="spinner" />
    </div>
  );

  if (!selected) return (
    <div style={{ padding: 40, textAlign: "center" }}>
      <p style={{ color: "var(--text-muted)" }}>Business not found.</p>
      <button className="btn btn-outline" onClick={() => navigate("/admin/businesses")} style={{ marginTop: 16 }}>← Back</button>
    </div>
  );

  const hasCreditSaved = savedCredit.creditLimit != null || savedCredit.creditTenureDays != null || savedCredit.creditTerms;
  const usedPct = creditSummary && creditSummary.creditLimit > 0
    ? Math.min(100, (creditSummary.usedCredit / creditSummary.creditLimit) * 100) : 0;

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <style>{`
        .biz-grid { display: grid; gap: 16px; }
        .biz-grid-3 { grid-template-columns: repeat(3, 1fr); }
        .biz-grid-2 { grid-template-columns: 1fr 1fr; }
        .biz-grid-2-1 { grid-template-columns: 2fr 1fr; }
        .biz-contact-table { width: 100%; border-collapse: collapse; font-size: 0.84rem; }
        .biz-contact-table th { padding: 9px 12px; text-align: left; font-size: 0.72rem; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid var(--border); white-space: nowrap; }
        .biz-contact-table td { padding: 11px 12px; border-bottom: 1px solid var(--border); vertical-align: middle; color: #333; }
        .biz-contact-table tr:last-child td { border-bottom: none; }
        .biz-contact-table tr:hover td { background: var(--bg-soft); }
        .biz-doc-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; }
        .biz-doc-item { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 14px 8px; border: 1px solid var(--border); border-radius: 10px; text-align: center; }
        .biz-doc-preview { width: 52px; height: 52px; object-fit: contain; border-radius: 6px; }
        .biz-doc-placeholder { width: 52px; height: 52px; background: var(--bg-soft); border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 1.4rem; }
        .biz-credit-bar { height: 8px; border-radius: 99px; background: #f0f0f0; overflow: hidden; margin: 12px 0; }
        .biz-credit-bar-fill { height: 100%; border-radius: 99px; transition: width 0.4s; }
        .biz-stat-chip { background: var(--bg-soft); border-radius: 10px; padding: 12px 14px; text-align: center; }
        .biz-stat-chip-label { font-size: 0.7rem; color: var(--text-muted); margin-bottom: 3px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
        .biz-stat-chip-value { font-size: 0.92rem; font-weight: 700; }
        .biz-credit-history-row { display: grid; grid-template-columns: 1.4fr 1fr 0.9fr 0.9fr 0.7fr; gap: 4px; padding: 8px 10px; border-bottom: 1px solid var(--border); font-size: 0.81rem; align-items: center; }
        .biz-credit-history-header { font-size: 0.7rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.04em; background: var(--bg-soft); border-radius: 6px 6px 0 0; }
        .badge { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 100px; font-size: 0.72rem; font-weight: 600; }
        .badge-green { background: rgba(5,150,105,0.1); color: #059669; }
        .badge-amber { background: rgba(245,158,11,0.1); color: #d97706; }
        .badge-red { background: rgba(239,68,68,0.1); color: #ef4444; }
        .badge-blue { background: rgba(0,120,191,0.1); color: var(--primary); }
        .biz-kebab-menu { position: relative; }
        .biz-kebab-dropdown { position: absolute; right: 0; top: 100%; background: #fff; border: 1px solid var(--border); border-radius: 8px; min-width: 130px; box-shadow: var(--shadow-md); z-index: 100; padding: 4px; }
        .biz-kebab-option { display: flex; align-items: center; gap: 8px; width: 100%; padding: 8px 10px; font-size: 0.82rem; cursor: pointer; border: none; background: none; border-radius: 6px; font-family: var(--font-body); text-align: left; }
        .biz-kebab-option:hover { background: var(--bg-soft); }
        .biz-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 16px; }
        .biz-action-btn { display: inline-flex; align-items: center; gap: 6px; padding: 9px 20px; border-radius: 8px; font-size: 0.85rem; font-weight: 600; cursor: pointer; border: none; font-family: var(--font-body); transition: opacity 0.15s; }
        .biz-action-btn:hover { opacity: 0.88; }
        .biz-action-btn-approve { background: #059669; color: #fff; }
        .biz-action-btn-revoke { background: #ef4444; color: #fff; }
        @media (max-width: 900px) {
          .biz-grid-3 { grid-template-columns: 1fr 1fr; }
          .biz-grid-2-1 { grid-template-columns: 1fr; }
          .biz-doc-grid { grid-template-columns: repeat(3, 1fr); }
          .biz-credit-history-row { grid-template-columns: 1.2fr 0.9fr 0.8fr 0.8fr 0.7fr; font-size: 0.76rem; }
        }
        @media (max-width: 640px) {
          .biz-grid-3 { grid-template-columns: 1fr; }
          .biz-grid-2 { grid-template-columns: 1fr; }
          .biz-doc-grid { grid-template-columns: repeat(2, 1fr); }
          .biz-form-grid { grid-template-columns: 1fr; }
          .biz-contact-table { display: block; overflow-x: auto; }
          .biz-credit-history-row { grid-template-columns: 1fr 1fr; gap: 4px 12px; }
          .biz-credit-history-row > span:nth-child(3),
          .biz-credit-history-row > span:nth-child(4) { display: none; }
        }
      `}</style>

      {/* ── Update existing documents panel ── */}
      {sync && (() => {
        const ch = sync.info.changedFields || [];
        const c = sync.counts;
        const canIssued = ch.some((k) => ["companyName", "gstNo", "companyAddress"].includes(k));
        const canReceipt = ch.some((k) => ["companyName", "customerPhone"].includes(k));
        const wantsFinal = sync.opts.finalOrders || sync.opts.bills || sync.opts.receipts;
        const setOpt = (k) => (e) => setSync((s) => ({ ...s, opts: { ...s.opts, [k]: e.target.checked } }));
        const row = (k, label, count, disabled, note) => (
          <label key={k} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", opacity: disabled ? 0.5 : 1, fontSize: "0.86rem" }}>
            <input type="checkbox" checked={!!sync.opts[k]} disabled={disabled} onChange={setOpt(k)} style={{ marginTop: 3 }} />
            <span><strong>{count}</strong> {label}{note && <span style={{ display: "block", fontSize: "0.75rem", color: "var(--text-muted)" }}>{note}</span>}</span>
          </label>
        );
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
            <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 560, maxHeight: "90vh", overflowY: "auto", padding: "24px 26px" }}>
              <h3 style={{ margin: "0 0 4px", fontSize: "1.1rem" }}>Update existing documents?</h3>
              <p style={{ margin: "0 0 14px", fontSize: "0.84rem", color: "var(--text-muted)" }}>
                The business is saved. New orders and invoices will use the new details. Older documents keep a saved copy of the old details.
              </p>
              <div style={{ background: "var(--bg-soft)", borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: "0.82rem" }}>
                {ch.map((k) => (
                  <div key={k} style={{ padding: "3px 0" }}>
                    <strong>{SYNC_LABELS[k] || k}:</strong>{" "}
                    <span style={{ color: "#999", textDecoration: "line-through" }}>{sync.info.oldValues[k] || "—"}</span>{" "}
                    → <span>{sync.info.newValues[k] || "—"}</span>
                  </div>
                ))}
              </div>

              {row("openOrders", "open orders (draft, confirmed)", c.ordersOpen, !c.ordersOpen, "Safe to update. Amounts and dates are never changed.")}

              <div style={{ borderTop: "1px solid var(--border)", marginTop: 8, paddingTop: 8 }}>
                <div style={{ fontSize: "0.78rem", fontWeight: 700, marginBottom: 2 }}>Issued documents</div>
                {!isAdmin ? (
                  <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", padding: "6px 0" }}>Only an admin can update delivered orders, invoices and receipts.</div>
                ) : (
                  <>
                    {row("finalOrders", "delivered orders", c.ordersFinal, !c.ordersFinal || !canIssued)}
                    {row("bills", "invoices", c.billsFinal, !c.billsFinal || !canIssued, "Invoices are tax documents. Check with your CA before changing a GST number or name on an issued invoice.")}
                    {row("receipts", "payment receipts", c.receiptsFinal, !c.receiptsFinal || !canReceipt, "Receipts only store company name and phone.")}
                    {wantsFinal && (
                      <label style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, padding: "10px 12px", marginTop: 8, fontSize: "0.82rem" }}>
                        <input type="checkbox" checked={sync.confirm} onChange={(e) => setSync((s) => ({ ...s, confirm: e.target.checked }))} style={{ marginTop: 3 }} />
                        <span>I understand this changes issued documents. This cannot be undone from here.</span>
                      </label>
                    )}
                  </>
                )}
              </div>

              <p style={{ fontSize: "0.74rem", color: "var(--text-muted)", margin: "12px 0 0" }}>Quotations are not linked to a business record, so they are not included.</p>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
                <button onClick={() => setSync(null)} disabled={sync.busy} style={{ padding: "8px 16px", border: "1px solid var(--border)", borderRadius: 8, background: "#fff", cursor: "pointer", fontFamily: "var(--font-body)" }}>
                  Skip
                </button>
                <button onClick={runSync} disabled={sync.busy || (!sync.opts.openOrders && !wantsFinal)} className="biz-action-btn biz-action-btn-approve">
                  {sync.busy ? "Updating…" : "Update documents"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Back Button ── */}
      <div style={{ marginBottom: 16 }}>
        <button
          onClick={() => navigate("/admin/businesses")}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px", border: "1px solid var(--border)", borderRadius: 8, cursor: "pointer", fontSize: "0.85rem", background: "#fff", fontFamily: "var(--font-body)" }}
        >
          <FiArrowLeft size={15} /> Back
        </button>
      </div>

      {/* ── Company Header Box ── */}
      <div style={{
        background: "#fff", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)",
        padding: "20px 24px", marginBottom: 20,
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 14,
      }}>
        <div>
          {nameEdit ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input
                className="form-control"
                value={nameValue}
                autoFocus
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveCompanyName(); if (e.key === "Escape") setNameEdit(false); }}
                style={{ minWidth: 260, fontSize: "1rem", fontWeight: 700 }}
              />
              <button onClick={saveCompanyName} disabled={nameSaving} className="biz-action-btn biz-action-btn-approve" style={{ padding: "7px 14px" }}>
                <FiSave size={14} /> {nameSaving ? "Saving…" : "Save name"}
              </button>
              <button onClick={() => setNameEdit(false)} style={{ padding: "7px 12px", border: "1px solid var(--border)", borderRadius: 8, background: "#fff", cursor: "pointer", fontFamily: "var(--font-body)", fontSize: "0.85rem" }}>
                Cancel
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <h2 style={{ margin: 0, fontSize: "1.35rem", fontWeight: 800, color: "#111" }}>{selected.companyName}</h2>
              <button
                onClick={() => { setNameValue(selected.companyName || ""); setNameEdit(true); }}
                title="Edit company name"
                style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", border: "1px solid var(--border)", borderRadius: 6, cursor: "pointer", fontSize: "0.78rem", background: "#fff", fontFamily: "var(--font-body)", color: "var(--text-muted)" }}
              >
                <FiEdit2 size={12} /> Edit
              </button>
            </div>
          )}
          <div style={{ fontSize: "0.82rem", color: "var(--text-muted)", marginTop: 3 }}>
            {COMPANY_TYPES[selected.companyType] || selected.companyType}
            {selected.industry && <> &nbsp;·&nbsp; {selected.industry}</>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span className={`badge ${isVerifiedLive ? "badge-green" : "badge-amber"}`} style={{ padding: "5px 14px", fontSize: "0.78rem" }}>
            {isVerifiedLive ? "✅ Approved" : "⏳ Pending"}
          </span>
          {!isVerifiedLive ? (
            <button className="biz-action-btn biz-action-btn-approve" onClick={() => handleVerify(true)}>
              <FiCheck size={15} /> Approve Business
            </button>
          ) : (
            <button className="biz-action-btn biz-action-btn-revoke" onClick={() => handleVerify(false)}>
              <FiX size={15} /> Revoke Approval
            </button>
          )}
        </div>
      </div>

      {/* ── Row 1: Contact info | GSTIN/PAN/TAN | Company Info ── */}
      <div className="biz-grid biz-grid-3" style={{ marginBottom: 16 }}>

        {/* Contact info */}
        <Card>
          <SectionTitle
            icon="✉️"
            editMode={contactInfoEdit}
            onEdit={() => { setContactInfoForm({ contactPersonEmail: selected.contactPersonEmail || "", contactPersonPhone: selected.contactPersonPhone || "", website: selected.website || "" }); setContactInfoEdit(true); }}
            onSave={() => saveSection(contactInfoForm, setContactInfoSaving, setContactInfoEdit, "Contact info saved!")}
            onCancel={() => setContactInfoEdit(false)}
            saving={contactInfoSaving}
          >
            Contact Info
          </SectionTitle>
          {contactInfoEdit ? (
            <>
              <EditableField label="Contact Email" fieldKey="contactPersonEmail" editForm={contactInfoForm} setEditForm={setContactInfoForm} editMode={true} type="email" />
              <EditableField label="Contact Phone" fieldKey="contactPersonPhone" editForm={contactInfoForm} setEditForm={setContactInfoForm} editMode={true} type="tel" />
              <EditableField label="Website" fieldKey="website" editForm={contactInfoForm} setEditForm={setContactInfoForm} editMode={true} type="url" />
            </>
          ) : (
            <>
              {selected.contactPersonEmail && (
                <div style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "flex-start" }}>
                  <span style={{ fontSize: "0.9rem", marginTop: 2 }}>✉️</span>
                  <div>
                    <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Contact Email</div>
                    <a href={`mailto:${selected.contactPersonEmail}`} style={{ fontSize: "0.88rem", color: "var(--primary)", textDecoration: "none" }}>{selected.contactPersonEmail}</a>
                  </div>
                </div>
              )}
              {selected.contactPersonPhone && (
                <div style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "flex-start" }}>
                  <span style={{ fontSize: "0.9rem", marginTop: 2 }}>📞</span>
                  <div>
                    <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Contact Phone</div>
                    <div style={{ fontSize: "0.88rem", color: "#111" }}>{selected.contactPersonPhone}</div>
                  </div>
                </div>
              )}
              {selected.website && (
                <div style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "flex-start" }}>
                  <span style={{ fontSize: "0.9rem", marginTop: 2 }}>🌐</span>
                  <div>
                    <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Website</div>
                    <a href={selected.website} target="_blank" rel="noreferrer" style={{ fontSize: "0.88rem", color: "var(--primary)", textDecoration: "none", wordBreak: "break-all" }}>{selected.website}</a>
                  </div>
                </div>
              )}
              {selected.regAddressLine1 && (
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <span style={{ fontSize: "0.9rem", marginTop: 2 }}>📍</span>
                  <div>
                    <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>Registered Address</div>
                    <div style={{ fontSize: "0.85rem", color: "#444", lineHeight: 1.75 }}>
                      {selected.regAddressLine1 && <div>{selected.regAddressLine1}</div>}
                      {selected.regAddressLine2 && <div>{selected.regAddressLine2}</div>}
                      <div>{[selected.regCity, selected.regPincode].filter(Boolean).join(", ")}</div>
                      <div>{[selected.regState && `${selected.regState}`, "India"].filter(Boolean).join(", ")}</div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>

        {/* GSTIN / PAN / TAN */}
        <Card>
          <SectionTitle
            icon="🪪"
            editMode={taxEdit}
            onEdit={() => { setTaxForm({ gstNumber: selected.gstNumber || "", panNumber: selected.panNumber || "", tanNumber: selected.tanNumber || "" }); setTaxEdit(true); }}
            onSave={() => saveSection(taxForm, setTaxSaving, setTaxEdit, "Tax info saved!")}
            onCancel={() => setTaxEdit(false)}
            saving={taxSaving}
          >
            Tax Info
          </SectionTitle>
          {[
            { label: "GSTIN", fieldKey: "gstNumber", value: selected.gstNumber, icon: "🪪", form: taxForm, setForm: setTaxForm },
            { label: "PAN", fieldKey: "panNumber", value: selected.panNumber, icon: "🗂", form: taxForm, setForm: setTaxForm },
            { label: "TAN", fieldKey: "tanNumber", value: selected.tanNumber, icon: "🔖", form: taxForm, setForm: setTaxForm },
          ].map(({ label, value, icon, fieldKey }) => (
            taxEdit ? (
              <EditableField key={label} label={label} fieldKey={fieldKey} editForm={taxForm} setEditForm={setTaxForm} editMode={true} mono />
            ) : (
              <div key={label} style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "flex-start" }}>
                <span style={{ fontSize: "1rem", marginTop: 1 }}>{icon}</span>
                <div>
                  <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
                  <div style={{ fontFamily: "monospace", fontSize: "0.88rem", color: value ? "#111" : "#ccc", fontStyle: value ? "normal" : "italic" }}>{value || "—"}</div>
                </div>
              </div>
            )
          ))}
        </Card>

        {/* TDS deducted by this customer (per financial year) */}
        <BusinessTdsCard businessId={selected.id} cardStyle={{ background: "#fff", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", padding: "22px 24px" }} />

        {/* Company Info */}
        <Card>
          <SectionTitle
            editMode={companyInfoEdit}
            onEdit={() => { setCompanyInfoForm({ companyType: selected.companyType || "", industry: selected.industry || "", udyamNumber: selected.udyamNumber || "" }); setCompanyInfoEdit(true); }}
            onSave={() => saveSection(companyInfoForm, setCompanyInfoSaving, setCompanyInfoEdit, "Company info saved!")}
            onCancel={() => setCompanyInfoEdit(false)}
            saving={companyInfoSaving}
          >
            Company Info
          </SectionTitle>
          <EditableField label="Company Type" fieldKey="companyType" value={COMPANY_TYPES[selected.companyType] || selected.companyType} editForm={companyInfoForm} setEditForm={setCompanyInfoForm} editMode={companyInfoEdit} options={COMPANY_TYPES} />
          <EditableField label="Industry" fieldKey="industry" value={selected.industry} editForm={companyInfoForm} setEditForm={setCompanyInfoForm} editMode={companyInfoEdit} />
          <EditableField label="Udyam Number" fieldKey="udyamNumber" value={selected.udyamNumber} editForm={companyInfoForm} setEditForm={setCompanyInfoForm} editMode={companyInfoEdit} mono />
          <InfoField label="Registered On" value={selected.createdAt ? new Date(selected.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : null} />
        </Card>
      </div>

      {/* ── Row 2: Primary Contact | Additional Contacts ── */}
      <div className="biz-grid biz-grid-2" style={{ marginBottom: 16, gridTemplateColumns: "280px 1fr" }}>

        {/* Primary Contact */}
        <Card>
          <SectionTitle
            icon="👤"
            editMode={primaryContactEdit}
            onEdit={() => { setPrimaryContactForm({ contactPersonName: selected.contactPersonName || "", contactPersonDesignation: selected.contactPersonDesignation || "", contactPersonPhone: selected.contactPersonPhone || "", contactPersonEmail: selected.contactPersonEmail || "" }); setPrimaryContactEdit(true); }}
            onSave={() => saveSection(primaryContactForm, setPrimaryContactSaving, setPrimaryContactEdit, "Primary contact saved!")}
            onCancel={() => setPrimaryContactEdit(false)}
            saving={primaryContactSaving}
          >
            Primary Contact
          </SectionTitle>
          <EditableField label="Name" fieldKey="contactPersonName" value={selected.contactPersonName} editForm={primaryContactForm} setEditForm={setPrimaryContactForm} editMode={primaryContactEdit} />
          <EditableField label="Designation" fieldKey="contactPersonDesignation" value={selected.contactPersonDesignation} editForm={primaryContactForm} setEditForm={setPrimaryContactForm} editMode={primaryContactEdit} />
          <EditableField label="Phone" fieldKey="contactPersonPhone" value={selected.contactPersonPhone} editForm={primaryContactForm} setEditForm={setPrimaryContactForm} editMode={primaryContactEdit} type="tel" />
          <EditableField label="Email" fieldKey="contactPersonEmail" value={selected.contactPersonEmail} editForm={primaryContactForm} setEditForm={setPrimaryContactForm} editMode={primaryContactEdit} type="email" />
          
          {selected.user && (
            <div style={{ marginTop: 10, paddingTop: 12, borderTop: "1px solid var(--border)", fontSize: "0.78rem", color: "var(--text-muted)" }}>
              👤 Registered by <strong>{selected.user.name}</strong>
            </div>
          )}
        </Card>

        {/* Additional Contacts */}
        <Card style={{ padding: "22px 0", overflow: "hidden" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 24px", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: "var(--primary)" }}>👥</span>
              <span style={{ fontWeight: 700, fontSize: "0.92rem" }}>Additional Contacts</span>
              {contacts.length > 0 && <span className="badge badge-blue">{contacts.length}</span>}
            </div>
            {!showContactForm && (
              <button
                onClick={openAddContact}
                style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 14px", border: "1px solid var(--primary)", borderRadius: 7, cursor: "pointer", fontSize: "0.8rem", color: "var(--primary)", background: "rgba(0,120,191,0.05)", fontFamily: "var(--font-body)", fontWeight: 600 }}
              >
                <FiPlus size={14} /> Add Contact
              </button>
            )}
          </div>

          {/* Inline add/edit form */}
          {showContactForm && (
            <div style={{ margin: "0 24px 16px", border: "1px solid var(--primary)", borderRadius: 10, padding: "18px 20px", background: "rgba(0,120,191,0.02)" }}>
              <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "var(--primary)", marginBottom: 14 }}>
                {editingContact ? "✏️ Edit Contact" : "➕ New Contact"}
              </div>
              <div className="biz-form-grid">
                {[["Name *", "name", "text"], ["Designation", "designation", "text"], ["Department", "department", "text"], ["Phone", "phone", "tel"], ["Email", "email", "email"]].map(([label, key, type]) => (
                  <div key={key}>
                    <label style={{ fontSize: "0.75rem", color: "var(--text-muted)", display: "block", marginBottom: 4 }}>{label}</label>
                    <input type={type} className="form-control" value={contactForm[key]} onChange={(e) => setContactForm((p) => ({ ...p, [key]: e.target.value }))} style={{ width: "100%", boxSizing: "border-box", fontSize: "0.85rem" }} />
                  </div>
                ))}
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={{ fontSize: "0.75rem", color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Notes</label>
                  <textarea className="form-control" rows={2} value={contactForm.notes} onChange={(e) => setContactForm((p) => ({ ...p, notes: e.target.value }))} style={{ width: "100%", boxSizing: "border-box", fontSize: "0.85rem" }} />
                </div>
                <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 8 }}>
                  <input type="checkbox" id="isPrimaryChk" checked={contactForm.isPrimary} onChange={(e) => setContactForm((p) => ({ ...p, isPrimary: e.target.checked }))} />
                  <label htmlFor="isPrimaryChk" style={{ fontSize: "0.83rem", cursor: "pointer" }}>Mark as primary contact</label>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                <button onClick={saveContact} disabled={savingContact} className="btn btn-primary" style={{ minWidth: 90 }}>
                  {savingContact ? "Saving…" : editingContact ? "Update" : "Save"}
                </button>
                <button onClick={() => setShowContactForm(false)} className="btn btn-outline" style={{ minWidth: 80 }}>Cancel</button>
              </div>
            </div>
          )}

          {contactsLoading ? (
            <div style={{ padding: "24px", textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem" }}>Loading…</div>
          ) : contacts.length === 0 && !showContactForm ? (
            <div style={{ padding: "28px 24px", textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem", borderTop: "1px solid var(--border)" }}>
              No additional contacts yet.
            </div>
          ) : contacts.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table className="biz-contact-table">
                <thead>
                  <tr>
                    {["Name", "Designation", "Department", "Phone", "Email", "Actions"].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c) => {
                    const isActive = c.isActive !== false;
                    return (
                      <tr key={c.id} style={{ opacity: isActive ? 1 : 0.5 }}>
                        <td>
                          <div style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{c.name}</div>
                          {c.isPrimary && <span className="badge badge-blue" style={{ marginTop: 2, fontSize: "0.65rem" }}>PRIMARY</span>}
                          {!isActive && <span className="badge badge-red" style={{ marginTop: 2, fontSize: "0.65rem" }}>INACTIVE</span>}
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>{c.designation || "—"}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{c.department || "—"}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{c.phone || "—"}</td>
                        
                        <td>
                          {c.email
                            ? <a href={`mailto:${c.email}`} style={{ color: "var(--primary)", textDecoration: "none", fontSize: "0.82rem" }}>{c.email}</a>
                            : "—"}
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                            <button
                              onClick={() => openEditContact(c)}
                              style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "3px 9px", border: "1px solid var(--border)", borderRadius: 5, cursor: "pointer", fontSize: "0.72rem", background: "#fff", fontFamily: "var(--font-body)", color: "var(--primary)", fontWeight: 600, whiteSpace: "nowrap" }}
                            >
                              <FiEdit2 size={11} /> Edit
                            </button>
                            <button
                              onClick={() => deactivateContact(c)}
                              style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "3px 9px", border: `1px solid ${isActive ? "rgba(245,158,11,0.35)" : "rgba(5,150,105,0.35)"}`, borderRadius: 5, cursor: "pointer", fontSize: "0.72rem", background: isActive ? "rgba(245,158,11,0.05)" : "rgba(5,150,105,0.05)", fontFamily: "var(--font-body)", color: isActive ? "#d97706" : "#059669", fontWeight: 600, whiteSpace: "nowrap" }}
                            >
                              {isActive ? "⏸ Deactivate" : "✅ Activate"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </Card>
      </div>

      {/* ── Row 3: Registered Address | Communication Address ── */}
      <div className="biz-grid biz-grid-2" style={{ marginBottom: 16 }}>
        <Card>
          <SectionTitle
            icon="📍"
            editMode={regAddressEdit}
            onEdit={() => { setRegAddressForm({ regAddressLine1: selected.regAddressLine1 || "", regAddressLine2: selected.regAddressLine2 || "", regCity: selected.regCity || "", regState: selected.regState || "", regPincode: selected.regPincode || "" }); setRegAddressEdit(true); }}
            onSave={() => saveSection(regAddressForm, setRegAddressSaving, setRegAddressEdit, "Registered address saved!")}
            onCancel={() => setRegAddressEdit(false)}
            saving={regAddressSaving}
          >
            Registered Address
          </SectionTitle>
          {regAddressEdit ? (
            <>
              <EditableField label="Address Line 1" fieldKey="regAddressLine1" editForm={regAddressForm} setEditForm={setRegAddressForm} editMode={true} />
              <EditableField label="Address Line 2" fieldKey="regAddressLine2" editForm={regAddressForm} setEditForm={setRegAddressForm} editMode={true} />
              <EditableField label="City" fieldKey="regCity" editForm={regAddressForm} setEditForm={setRegAddressForm} editMode={true} />
              <EditableField label="State" fieldKey="regState" editForm={regAddressForm} setEditForm={setRegAddressForm} editMode={true} />
              <EditableField label="Pincode" fieldKey="regPincode" editForm={regAddressForm} setEditForm={setRegAddressForm} editMode={true} />
            </>
          ) : (
            <div style={{ fontSize: "0.88rem", color: "#444", lineHeight: 1.85 }}>
              {selected.regAddressLine1 ? <div>{selected.regAddressLine1}</div> : <div style={{ color: "#ccc", fontStyle: "italic" }}>—</div>}
              {selected.regAddressLine2 && <div>{selected.regAddressLine2}</div>}
              {selected.regCity && <div>{selected.regCity}{selected.regPincode ? `, ${selected.regPincode}` : ""}</div>}
              {(selected.regState || true) && <div>{[selected.regState, "India"].filter(Boolean).join(", ")}</div>}
            </div>
          )}
        </Card>
        <Card>
          <SectionTitle
            icon="📍"
            editMode={commAddressEdit}
            onEdit={() => { setCommAddressForm({ commAddressLine1: selected.commAddressLine1 || "", commAddressLine2: selected.commAddressLine2 || "", commCity: selected.commCity || "", commState: selected.commState || "", commPincode: selected.commPincode || "" }); setCommAddressEdit(true); }}
            onSave={() => saveSection(commAddressForm, setCommAddressSaving, setCommAddressEdit, "Communication address saved!")}
            onCancel={() => setCommAddressEdit(false)}
            saving={commAddressSaving}
          >
            Communication Address
          </SectionTitle>
          {commAddressEdit ? (
            <>
              <EditableField label="Address Line 1" fieldKey="commAddressLine1" editForm={commAddressForm} setEditForm={setCommAddressForm} editMode={true} />
              <EditableField label="Address Line 2" fieldKey="commAddressLine2" editForm={commAddressForm} setEditForm={setCommAddressForm} editMode={true} />
              <EditableField label="City" fieldKey="commCity" editForm={commAddressForm} setEditForm={setCommAddressForm} editMode={true} />
              <EditableField label="State" fieldKey="commState" editForm={commAddressForm} setEditForm={setCommAddressForm} editMode={true} />
              <EditableField label="Pincode" fieldKey="commPincode" editForm={commAddressForm} setEditForm={setCommAddressForm} editMode={true} />
            </>
          ) : (
            <div style={{ fontSize: "0.88rem", color: "#444", lineHeight: 1.85 }}>
              {selected.commAddressLine1 ? (
                <>
                  <div>{selected.commAddressLine1}</div>
                  {selected.commAddressLine2 && <div>{selected.commAddressLine2}</div>}
                  {selected.commCity && <div>{selected.commCity}{selected.commPincode ? `, ${selected.commPincode}` : ""}</div>}
                  <div>{[selected.commState, "India"].filter(Boolean).join(", ")}</div>
                </>
              ) : (
                <div style={{ color: "#ccc", fontStyle: "italic" }}>Same as registered address</div>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* ── Row 4: Bank Details | Documents ── */}
      <div className="biz-grid biz-grid-2" style={{ marginBottom: 16, gridTemplateColumns: "280px 1fr" }}>

        {/* Bank Details */}
        <Card>
          <SectionTitle
            icon="🏦"
            editMode={bankEdit}
            onEdit={() => { setBankForm({ bankName: selected.bankName || "", branchName: selected.branchName || "", ifscCode: selected.ifscCode || "", accountNumber: selected.accountNumber || "" }); setBankEdit(true); }}
            onSave={() => saveSection(bankForm, setBankSaving, setBankEdit, "Bank details saved!")}
            onCancel={() => setBankEdit(false)}
            saving={bankSaving}
          >
            Bank Details
          </SectionTitle>
          <EditableField label="Bank Name" fieldKey="bankName" value={selected.bankName} editForm={bankForm} setEditForm={setBankForm} editMode={bankEdit} />
          <EditableField label="Branch" fieldKey="branchName" value={selected.branchName} editForm={bankForm} setEditForm={setBankForm} editMode={bankEdit} />
          <EditableField label="IFSC Code" fieldKey="ifscCode" value={selected.ifscCode} editForm={bankForm} setEditForm={setBankForm} editMode={bankEdit} mono />
          <EditableField label="Account Number" fieldKey="accountNumber" value={selected.accountNumber} editForm={bankForm} setEditForm={setBankForm} editMode={bankEdit} mono />
        </Card>

        {/* Documents */}
        <Card>
          <SectionTitle icon="📄">Documents</SectionTitle>
          <div className="biz-doc-grid">
            {DOC_FIELDS.map(({ label, field, isImage }) => ({ label, field, isImage, url: selected[field] })).map(({ label, field, url, isImage }) => (
              <div key={label} className="biz-doc-item">
                {url ? (
                  isImage
                    ? <img src={url} alt={label} className="biz-doc-preview" />
                    : <div className="biz-doc-placeholder">📋</div>
                ) : (
                  <div className="biz-doc-placeholder" style={{ color: "#ccc" }}>📄</div>
                )}
                <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "#333", lineHeight: 1.3, textAlign: "center" }}>{label}</div>
                {url ? (
                  <a href={url} target="_blank" rel="noreferrer" download style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.7rem", color: "var(--primary)", textDecoration: "none" }}>
                    {url.match(/\.pdf/i) ? "PDF" : "PNG"} ↓
                  </a>
                ) : (
                  <span style={{ fontSize: "0.7rem", color: "#ccc", fontStyle: "italic" }}>Not uploaded</span>
                )}
                <input
                  type="file"
                  accept={isImage ? ".png,.jpg,.jpeg,.svg" : ".pdf,.png,.jpg,.jpeg"}
                  style={{ display: "none" }}
                  ref={(el) => { docInputs.current[field] = el; }}
                  onChange={(e) => uploadDoc(field, e.target.files[0])}
                />
                <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
                  <button
                    onClick={() => docInputs.current[field]?.click()}
                    disabled={docBusy === field}
                    style={{ padding: "2px 8px", fontSize: "0.68rem", border: "1px solid var(--border)", borderRadius: 5, background: "#fff", cursor: "pointer", fontFamily: "var(--font-body)" }}
                  >
                    {docBusy === field ? "Uploading…" : url ? "Replace" : "Upload"}
                  </button>
                  {url && (
                    <button
                      onClick={() => removeDoc(field, label)}
                      disabled={docBusy === field}
                      style={{ padding: "2px 8px", fontSize: "0.68rem", border: "1px solid var(--border)", borderRadius: 5, background: "#fff", cursor: "pointer", color: "#ef4444", fontFamily: "var(--font-body)" }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* ── Row 5: Admin Notes | Credit ── */}
      <div className="biz-grid biz-grid-2" style={{ marginBottom: 16 }}>

        {/* Admin Notes */}
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid var(--border)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: "var(--primary)" }}>✏️</span>
              <span style={{ fontWeight: 700, fontSize: "0.92rem" }}>Admin Notes</span>
            </div>
            {!notesEditMode && (
              <button onClick={() => setNotesEditMode(true)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 12px", border: "1px solid var(--border)", borderRadius: 6, cursor: "pointer", fontSize: "0.78rem", background: "#fff", fontFamily: "var(--font-body)" }}>
                <FiEdit2 size={12} /> Edit
              </button>
            )}
          </div>
          {notesEditMode ? (
            <>
              <textarea
                className="form-control" rows={4}
                value={notesValue}
                onChange={(e) => setNotesValue(e.target.value)}
                placeholder="Add internal notes about this business..."
                style={{ width: "100%", boxSizing: "border-box", fontSize: "0.85rem", marginBottom: 10 }}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={saveNotes} disabled={savingNotes} className="btn btn-primary" style={{ minWidth: 80 }}>{savingNotes ? "Saving…" : "Save"}</button>
                <button onClick={() => { setNotesEditMode(false); setNotesValue(selected.notes || ""); }} className="btn btn-outline">Cancel</button>
              </div>
            </>
          ) : (
            <>
              {notesValue ? (
                <div style={{ fontSize: "0.88rem", color: "#444", lineHeight: 1.7, padding: "10px 14px", background: "rgba(245,158,11,0.04)", border: "1px solid rgba(245,158,11,0.18)", borderRadius: 8 }}>
                  {notesValue}
                </div>
              ) : (
                <div style={{ fontSize: "0.85rem", color: "#ccc", fontStyle: "italic" }}>No notes yet.</div>
              )}
              {selected.updatedAt && notesValue && (
                <div style={{ marginTop: 10, fontSize: "0.73rem", color: "var(--text-muted)" }}>
                  Reviewed on {new Date(selected.updatedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </div>
              )}
            </>
          )}
        </Card>

        {/* Credit settings */}
        <Card>
          <SectionTitle icon="💳">Credit Settings</SectionTitle>
          {!creditEditMode && hasCreditSaved ? (
            <div>
              <InfoField
  label="Credit Limit"
  value={
    savedCredit.creditLimit != null
      ? savedCredit.creditLimit === 0
        ? '⏸ Deactivated'
        : `₹ ${formatINR(savedCredit.creditLimit)}`
      : null
  }
/>
{savedCredit.creditLimit != null && savedCredit.creditLimit > 0 && (
  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: -10, marginBottom: 14, fontStyle: 'italic' }}>
    {numberToWords(savedCredit.creditLimit)}
  </div>
)}
              <InfoField label="Credit Tenure (Days)" value={savedCredit.creditTenureDays != null ? String(savedCredit.creditTenureDays) : null} />
              {savedCredit.creditTerms && <InfoField label="Terms & Conditions" value={savedCredit.creditTerms} />}
              {selected?.monthlyBillingEnabled && (
                <div style={{ fontSize: '0.75rem', color: '#ef4444', marginBottom: 10 }}>
                  ⚠️ Monthly billing is active. Disable it first before editing credit settings.
                </div>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                <button onClick={() => { setCredit({ creditLimit: savedCredit.creditLimit ?? "", creditTenureDays: savedCredit.creditTenureDays ?? "", creditTerms: savedCredit.creditTerms ?? "" }); setCreditEditMode(true); }} disabled={!!selected?.monthlyBillingEnabled} style={{ padding: "5px 12px", border: "1px solid var(--border)", borderRadius: 6, cursor: selected?.monthlyBillingEnabled ? "not-allowed" : "pointer", fontSize: "0.78rem", background: "#fff", fontFamily: "var(--font-body)", opacity: selected?.monthlyBillingEnabled ? 0.4 : 1 }}>
                  ✏️ Edit
                </button>
                <button onClick={toggleCreditActive} disabled={creditSaving || !!selected?.monthlyBillingEnabled} style={{ padding: "5px 12px", border: `1px solid ${savedCredit.creditLimit === 0 ? "rgba(5,150,105,0.3)" : "rgba(245,158,11,0.3)"}`, borderRadius: 6, cursor: selected?.monthlyBillingEnabled ? "not-allowed" : "pointer", fontSize: "0.78rem", color: savedCredit.creditLimit === 0 ? "#059669" : "#d97706", background: savedCredit.creditLimit === 0 ? "rgba(5,150,105,0.04)" : "rgba(245,158,11,0.04)", fontFamily: "var(--font-body)", opacity: selected?.monthlyBillingEnabled ? 0.4 : 1 }}>
                  {savedCredit.creditLimit === 0 ? "✅ Activate" : "⏸ Deactivate"}
                </button>
                <button onClick={deleteCredit} disabled={creditSaving || !!selected?.monthlyBillingEnabled} style={{ padding: "5px 12px", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 6, cursor: selected?.monthlyBillingEnabled ? "not-allowed" : "pointer", fontSize: "0.78rem", color: "#ef4444", background: "rgba(239,68,68,0.04)", fontFamily: "var(--font-body)", opacity: selected?.monthlyBillingEnabled ? 0.4 : 1 }}>
                  🗑 Delete
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--text-muted)", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Credit Limit (₹)</label>
                {selected?.monthlyBillingEnabled && (
                  <div style={{ fontSize: '0.75rem', color: '#ef4444', marginBottom: 6 }}>
                    ⚠️ This business is on monthly billing. Disable it first before assigning a credit limit.
                  </div>
                )}
                
<input
  type="text"
  className="form-control"
  value={credit.creditLimit !== '' ? Number(parseINRInput(String(credit.creditLimit))).toLocaleString('en-IN') : ''}
  onChange={(e) => {
    const raw = parseINRInput(e.target.value);
    if (raw === '' || /^\d+$/.test(raw)) {
      setCredit((c) => ({ ...c, creditLimit: raw }));
    }
  }}
  placeholder="e.g. 2,00,000"
  disabled={!!selected?.monthlyBillingEnabled}
  style={{ width: "100%", boxSizing: "border-box", opacity: selected?.monthlyBillingEnabled ? 0.5 : 1 }}
/>
{credit.creditLimit !== '' && Number(parseINRInput(String(credit.creditLimit))) > 0 && (
  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4, fontStyle: 'italic' }}>
    {numberToWords(Number(parseINRInput(String(credit.creditLimit))))}
  </div>
)}
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--text-muted)", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Credit Tenure (Days)</label>
                <input type="number" className="form-control" min={1} max={365} value={credit.creditTenureDays} onChange={(e) => setCredit((c) => ({ ...c, creditTenureDays: e.target.value }))} placeholder="e.g. 30" disabled={!!selected?.monthlyBillingEnabled} style={{ width: "100%", boxSizing: "border-box", opacity: selected?.monthlyBillingEnabled ? 0.5 : 1 }} />
                <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                  {[30, 45, 60, 90].map((d) => (
                    <button key={d} type="button" onClick={() => setCredit((c) => ({ ...c, creditTenureDays: d }))} style={{ padding: "3px 12px", borderRadius: 6, fontSize: "0.75rem", cursor: "pointer", fontFamily: "var(--font-body)", border: Number(credit.creditTenureDays) === d ? "1px solid var(--primary)" : "1px solid var(--border)", background: Number(credit.creditTenureDays) === d ? "rgba(0,120,191,0.08)" : "#fff", color: Number(credit.creditTenureDays) === d ? "var(--primary)" : "var(--text-muted)" }}>
                      {d}d
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--text-muted)", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Terms & Conditions</label>
                <textarea className="form-control" rows={3} value={credit.creditTerms} onChange={(e) => setCredit((c) => ({ ...c, creditTerms: e.target.value }))} placeholder="e.g. Payment due within tenure period..." disabled={!!selected?.monthlyBillingEnabled} style={{ width: "100%", boxSizing: "border-box", opacity: selected?.monthlyBillingEnabled ? 0.5 : 1 }} />
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={saveCredit} disabled={creditSaving || !!selected?.monthlyBillingEnabled} className="btn btn-primary" style={{ minWidth: 120, opacity: selected?.monthlyBillingEnabled ? 0.5 : 1, cursor: selected?.monthlyBillingEnabled ? "not-allowed" : "pointer" }}>{creditSaving ? "Saving…" : "💾 Save"}</button>
                {hasCreditSaved && <button onClick={() => setCreditEditMode(false)} className="btn btn-outline">Cancel</button>}
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* ── Row 6: Credit Usage | Credit History ── */}
      {hasCreditSaved && savedCredit.creditLimit > 0 && (
        <div className="biz-grid biz-grid-2" style={{ marginBottom: 16 }}>

          {/* Credit Usage */}
          <Card>
            <SectionTitle icon="📊">Credit Usage</SectionTitle>
            {creditSummaryLoading ? (
              <div style={{ color: "var(--text-muted)", fontSize: "0.84rem" }}>Loading…</div>
            ) : creditSummary ? (
              <>
                <div className="biz-grid biz-grid-3" style={{ gap: 10, marginBottom: 4 }}>
                  {[
                    { label: "Total Limit",    value: `₹ ${Number(creditSummary.creditLimit).toLocaleString("en-IN")}`,    color: "var(--primary)" },
                    { label: "Balance Pending", value: `₹ ${Number(creditSummary.usedCredit).toLocaleString("en-IN")}`,   color: "#ef4444" },
                    { label: "Available",       value: `₹ ${Number(creditSummary.availableCredit).toLocaleString("en-IN")}`, color: "#059669" },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="biz-stat-chip">
                      <div className="biz-stat-chip-label">{label}</div>
                      <div className="biz-stat-chip-value" style={{ color }}>{value}</div>
                    </div>
                  ))}
                </div>
                <div className="biz-credit-bar">
                  <div className="biz-credit-bar-fill" style={{ width: `${usedPct}%`, background: usedPct > 80 ? "#ef4444" : "var(--primary)" }} />
                </div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", textAlign: "center" }}>
                  {usedPct.toFixed(1)}% Used
                </div>
              </>
            ) : (
              <div style={{ color: "var(--text-muted)", fontSize: "0.84rem" }}>No usage data.</div>
            )}
          </Card>

          {/* Credit History */}
          <Card style={{ padding: "22px 0", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 24px", marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid var(--border)" }}>
              <span style={{ color: "var(--primary)" }}>📋</span>
              <span style={{ fontWeight: 700, fontSize: "0.92rem" }}>Credit History</span>
            </div>
            {creditSummaryLoading ? (
              <div style={{ padding: "16px 24px", color: "var(--text-muted)", fontSize: "0.84rem" }}>Loading…</div>
            ) : creditSummary?.creditHistory?.length > 0 ? (
              <div style={{ overflowX: "auto" }}>
                {/* 6 columns: Order No | Credit Amt | Received | Balance | Due Date | Status */}
                <div className="biz-credit-history-row biz-credit-history-header" style={{ padding: "6px 24px", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr" }}>
                  <span>Order No.</span>
                  <span style={{ textAlign: "right" }}>Credit Amt</span>
                  <span style={{ textAlign: "right" }}>Received</span>
                  <span style={{ textAlign: "right" }}>Balance</span>
                  <span style={{ textAlign: "center" }}>Due Date</span>
                  <span style={{ textAlign: "center" }}>Status</span>
                </div>
                {creditSummary.creditHistory.map((h, i) => {
                  const isOverdue = h.isOverdue;
                  const badgeClass = h.creditStatus === "settled" ? "badge-green" : isOverdue ? "badge-red" : "badge-amber";
                  const badgeLabel = h.creditStatus === "settled" ? "Settled" : isOverdue ? "Overdue" : "Pending";
                  return (
                    <div key={i} className="biz-credit-history-row" style={{ padding: "8px 24px", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr" }}>
                      <span style={{ fontWeight: 600, color: "var(--primary)" }}>{h.orderNumber}</span>
                      <span style={{ textAlign: "right", fontWeight: 600, color: "#111" }}>
                        ₹{Number(h.creditAmount || 0).toLocaleString("en-IN")}
                      </span>
                      <span style={{ textAlign: "right", fontWeight: 600, color: "#059669" }}>
                        ₹{Number(h.totalReceived || 0).toLocaleString("en-IN")}
                      </span>
                      <span style={{ textAlign: "right", fontWeight: 700, color: h.balanceLeft > 0 ? "#ef4444" : "#059669" }}>
                        ₹{Number(h.balanceLeft || 0).toLocaleString("en-IN")}
                      </span>
                      <span style={{ textAlign: "center", fontSize: "0.75rem", color: isOverdue ? "#ef4444" : "var(--text-muted)", fontWeight: isOverdue ? 700 : 400 }}>
                        {h.dueDate ? new Date(h.dueDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                        {isOverdue && " ⚠️"}
                      </span>
                      <span style={{ textAlign: "center" }}>
                        <span className={`badge ${badgeClass}`} style={{ fontSize: "0.65rem" }}>
                          {badgeLabel}
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ padding: "16px 24px", color: "var(--text-muted)", fontSize: "0.84rem", fontStyle: "italic" }}>No credit history yet.</div>
            )}
          </Card>
        </div>
      )}

 {/* ── Opening Balance ── */}
      <div style={{ marginBottom: 16 }}>
        <OpeningBalanceCard
          business={selected}
          isVerified={isVerifiedLive}
          canEdit={canEditOpeningBalance}
          onChanged={(updated) => setSelected((prev) => ({
            ...prev,
            opening_balance:      updated?.opening_balance ?? 0,
            opening_balance_type: updated?.opening_balance_type ?? "debit",
            opening_balance_date: updated?.opening_balance_date ?? null,
          }))}
        />
      </div>

 {/* ── Monthly Billing ── */}
      <div style={{ marginBottom: 16 }}>
        <MonthlyBillingCard
          businessId={id}
          companyName={selected?.companyName || ''}
          hasCreditLimit={savedCredit.creditLimit != null}
          onBillingSettingsChange={(updatedBilling) => {
            setSelected(prev => ({ ...prev, monthlyBillingEnabled: updatedBilling.monthlyBillingEnabled }));
          }}
        />
      </div>

      {/* ── Row 7: Terms & Conditions (if set) ── */}
      {savedCredit.creditTerms && (
        <Card style={{ marginBottom: 16 }}>
          <SectionTitle icon="📝">Terms &amp; Conditions</SectionTitle>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: "0.88rem", color: "#444", lineHeight: 2 }}>
            {savedCredit.creditTerms.split("\n").filter(Boolean).map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
};

export default AdminBusinessDetail;